import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a price from cents to dollars
 * @param price Amount in cents
 * @returns Formatted price in dollars
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price / 100);
}

/**
 * Format a date string to a readable format
 * @param dateString The date string to format
 * @returns Formatted date string
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { 
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Helper function to retry an asynchronous operation with exponential backoff
 * @param operation Function to execute
 * @param maxRetries Maximum number of retries
 * @param delay Initial delay in ms
 * @param onRetry Callback to execute on each retry
 * @returns Result of the operation
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 500,
  onRetry?: (attempt: number, error: any) => void
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Wait before retrying, except for the first attempt
      if (attempt > 0) {
        const backoffDelay = delay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
      
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (onRetry) {
        onRetry(attempt + 1, error);
      } else {
        console.error(`Operation failed (attempt ${attempt + 1}/${maxRetries}):`, error);
      }
    }
  }
  
  throw lastError;
}
